
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import EditorToolbar from "./editor/EditorToolbar";
import { useEffect } from "react";
import { ResizablePanelGroup, ResizablePanel } from "@/components/ui/resizable";
import { ImageUploadControls } from "./editor/components/ImageUploadControls";
import { ImageAlignmentControls } from "./editor/components/ImageAlignmentControls";

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
}

const RichTextEditor = ({ content, onChange }: RichTextEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: {
          keepMarks: true,
        },
      }),
      TextStyle,
      Color,
      Link.configure({
        openOnClick: false,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'newsletter-image',
        },
        allowBase64: true,
        inline: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content,
    editorProps: {
      attributes: {
        class: 'prose max-w-none focus:outline-none',
        style: 'overflow: hidden;',
      },
      handleClickOn: (view, pos, node, nodePos, event) => {
        // Handle image clicks to enable selection for alignment controls
        if (node.type.name === 'image') {
          console.log("[RichTextEditor] Image clicked at position:", nodePos);
          // The selection will be automatically updated by TipTap
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      console.log("[RichTextEditor] Content updated:", html);
      onChange(html);
    },
  });

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      console.log("[RichTextEditor] Updating editor content from prop:", content);
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border rounded-lg overflow-hidden flex flex-col">
      <EditorToolbar editor={editor} />
      
      <div className="bg-gray-50 p-3 border-b space-y-3">
        <ImageUploadControls editor={editor} />
        <ImageAlignmentControls editor={editor} />
      </div>
      
      <div className="p-4 min-h-[600px] bg-white overflow-auto">
        <EditorContent 
          editor={editor} 
          className="prose max-w-none focus:outline-none"
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
